import React from 'react';

const PitchContentFallback = ({ pitchContent }) => {
    if (!pitchContent || typeof pitchContent !== 'object') {
        return null;
    }

    const pitch =
        pitchContent.pitch && typeof pitchContent.pitch === 'object'
            ? pitchContent.pitch
            : null;

    const headline = pitch?.hook_headline;
    const summary = pitch?.executive_summary;

    return (
        <div
            style={{
                maxWidth: '720px',
                margin: '0 auto',
                padding: '20px 24px',
                backgroundColor: '#ffffff',
                borderRadius: '10px',
                border: '1px solid rgba(148, 163, 184, 0.35)',
                color: '#0f172a',
                fontSize: '14px',
                lineHeight: 1.55,
                maxHeight: '100%',
                overflow: 'auto',
            }}
        >
            {pitchContent.generated_at && (
                <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: '#64748b' }}>
                    Generated{' '}
                    <time dateTime={String(pitchContent.generated_at)}>
                        {(() => {
                            try {
                                const d = new Date(pitchContent.generated_at);
                                return Number.isNaN(d.getTime())
                                    ? String(pitchContent.generated_at)
                                    : d.toLocaleString();
                            } catch {
                                return String(pitchContent.generated_at);
                            }
                        })()}
                    </time>
                </p>
            )}
            {headline && (
                <h4 style={{ margin: '0 0 10px 0', fontSize: '17px', fontWeight: 600 }}>{headline}</h4>
            )}
            {summary && (
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{summary}</p>
            )}
            {!headline && !summary && (
                <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>
                    Structured pitch data is available, but no headline or summary fields were found.
                    Full HTML may be missing for this site.
                </p>
            )}
        </div>
    );
};

const SalesPitchErrorHint = () => (
    <p style={{ margin: '14px 0 0 0', fontSize: '12px', lineHeight: 1.55, color: '#57534e' }}>
        To generate a pitch, run batch job <strong>cms.WebsiteProspectPitch</strong> or job{' '}
        <strong>cms/build_sales_pitch</strong> (after <strong>cms/generate_sales_pitch</strong> for a
        single site). Analyze the site first for best results.
    </p>
);

function safePitchFileStem(pitchContent) {
    const raw = pitchContent && typeof pitchContent.url_hash === 'string' ? pitchContent.url_hash : '';
    const cleaned = raw.replace(/[^a-fA-F0-9]/g, '').slice(0, 40);
    return cleaned || 'pitch';
}

function downloadSalesPitchFile(html, pitchContent) {
    const stem = safePitchFileStem(pitchContent);
    if (html) {
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sales_pitch_${stem}.html`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        return;
    }
    if (pitchContent && typeof pitchContent === 'object') {
        const blob = new Blob([JSON.stringify(pitchContent, null, 2)], {
            type: 'application/json;charset=utf-8',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sales_pitch_content_${stem}.json`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }
}

const WebsiteSalesPitchModal = ({
    isOpen,
    onClose,
    isLoading,
    html,
    pitchContent,
    error,
}) => {
    if (!isOpen) {
        return null;
    }

    const showIframe = Boolean(html);
    const showFallback = !isLoading && !error && !html && pitchContent;
    const canDownload = !isLoading && !error && (Boolean(html) || Boolean(pitchContent));

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(7, 7, 10, 0.92)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
                padding: '24px',
            }}
            onClick={onClose}
        >
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '1280px',
                    height: '100%',
                    maxHeight: '860px',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 18px 60px rgba(0, 0, 0, 0.35)',
                    border: '1px solid rgba(148, 163, 184, 0.4)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '14px',
                        right: '14px',
                        backgroundColor: '#ef4444',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '999px',
                        width: '40px',
                        height: '40px',
                        fontSize: '22px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontWeight: 'bold',
                        boxShadow: '0 8px 20px rgba(239, 68, 68, 0.35)',
                        transition: 'transform 0.2s ease, background-color 0.2s ease',
                        zIndex: 1,
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#dc2626';
                        e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#ef4444';
                        e.target.style.transform = 'scale(1)';
                    }}
                >
                    ×
                </button>

                <div
                    style={{
                        padding: '20px 56px 12px 24px',
                        borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '16px',
                        backgroundColor: '#f9fafb',
                    }}
                >
                    <div style={{ minWidth: 0 }}>
                        <h3
                            style={{
                                margin: 0,
                                fontSize: '18px',
                                fontWeight: 600,
                                color: '#0f172a',
                            }}
                        >
                            CMS sales pitch
                        </h3>
                        <p
                            style={{
                                margin: '4px 0 0 0',
                                fontSize: '13px',
                                color: '#6b7280',
                            }}
                        >
                            Interactive pitch page (Chart.js, tabs). Tracking is console-only in the embed.
                        </p>
                    </div>
                    {canDownload && (
                        <button
                            type="button"
                            onClick={() => downloadSalesPitchFile(html, pitchContent)}
                            style={{
                                flexShrink: 0,
                                backgroundColor: '#ffffff',
                                color: '#0f172a',
                                border: '1px solid rgba(148, 163, 184, 0.85)',
                                borderRadius: '8px',
                                padding: '8px 14px',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                            }}
                            title={
                                html
                                    ? 'Download as HTML (open or print locally)'
                                    : 'Download structured pitch as JSON'
                            }
                        >
                            Download{html ? ' HTML' : ' JSON'}
                        </button>
                    )}
                </div>

                <div
                    style={{
                        flex: 1,
                        backgroundColor: '#f3f4f6',
                        padding: '16px',
                        minHeight: 0,
                    }}
                >
                    {isLoading && (
                        <div
                            style={{
                                height: '100%',
                                minHeight: '200px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#4b5563',
                                fontSize: '14px',
                                fontWeight: 500,
                            }}
                        >
                            Loading sales pitch…
                        </div>
                    )}

                    {!isLoading && error && (
                        <div
                            style={{
                                maxWidth: '520px',
                                margin: '40px auto',
                                padding: '16px 18px',
                                borderRadius: '8px',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                color: '#b91c1c',
                                fontSize: '13px',
                            }}
                        >
                            {error}
                            <SalesPitchErrorHint />
                        </div>
                    )}

                    {!isLoading && !error && showIframe && (
                        <iframe
                            title="CMS sales pitch"
                            srcDoc={html}
                            sandbox="allow-scripts allow-same-origin allow-popups"
                            style={{
                                width: '100%',
                                height: '100%',
                                minHeight: '80vh',
                                border: 'none',
                                borderRadius: '10px',
                                backgroundColor: '#ffffff',
                                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12)',
                            }}
                        />
                    )}

                    {!isLoading && !error && showFallback && (
                        <PitchContentFallback pitchContent={pitchContent} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default WebsiteSalesPitchModal;
